// Render-error containment.
//
// There was NO ErrorBoundary anywhere in this app. React's default on an
// uncaught render error is to unmount the entire tree, so one bad field
// from the API -- `undefined.toFixed()` in the screener, a missing options
// greek -- blanked the whole terminal until the trader manually reloaded.
// For a trading UI that is worse than a wrong number: the position blotter
// and the kill switch disappear with everything else.
//
// Wrap each tab so a failure is contained to that panel.

import React from 'react';
import { RED, MUT, FONT } from '@/theme';

interface Props {
  children: React.ReactNode;
  /** Shown in the fallback so the trader knows what died. */
  label?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the detail in the console for diagnosis; never swallow it.
    console.error(`[ErrorBoundary] ${this.props.label ?? 'panel'} failed to render`, error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ padding: 16, fontFamily: FONT, fontSize: 12 }}>
        <div style={{ color: RED, fontWeight: 'bold', marginBottom: 6 }}>
          {this.props.label ?? 'This panel'} failed to render.
        </div>
        <div style={{ color: MUT, marginBottom: 10 }}>
          The rest of the terminal is still live. This panel only.
        </div>
        <pre style={{
          color: MUT, fontSize: 10, whiteSpace: 'pre-wrap',
          background: '#111', padding: 8, borderRadius: 3, maxHeight: 160, overflow: 'auto',
        }}>
          {String(this.state.error?.message ?? this.state.error)}
        </pre>
        <button
          onClick={() => this.setState({ error: null })}
          style={{
            marginTop: 8, fontFamily: FONT, fontSize: 11, padding: '4px 10px',
            background: '#1a1a1a', color: '#e0e0e0', border: '1px solid #333', cursor: 'pointer',
          }}
        >
          Retry this panel
        </button>
      </div>
    );
  }
}
